import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/components/theme-provider';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from '@/lib/react-query-config';
import { Toaster } from 'sonner';
import { Provider } from 'react-redux';
import { store } from '@/lib/store/store';
import { ReduxHydrator } from '@/components/ReduxHydrator';
import { ScrollToTopOnRouteChange } from '@/components/ScrollToTopOnRouteChange';
import { ScrollToHashOnNavigation } from '@/components/ScrollToHashOnNavigation';
import { RouteAnalyticsTracker } from '@/components/RouteAnalyticsTracker';
import { TrackingCapture } from '@/components/TrackingCapture';
import ShopifyLayout from '@/layouts/ShopifyLayout';
import { PortalAuthProvider } from '@/lib/contexts/PortalAuthContext';
import RequirePortalAuth from '@/components/portal/RequirePortalAuth';
import PortalLayout from '@/components/portal/PortalLayout';
import { AuthHashRedirector } from '@/components/portal/AuthHashRedirector';

// Wrap React.lazy so that a failed dynamic import (typically a stale chunk
// hash after a new deployment) triggers a one-time full page reload to fetch
// the fresh asset manifest instead of showing a blank screen.
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    const STORAGE_KEY = 'bf:chunk-reloaded';
    try {
      const mod = await factory();
      window.sessionStorage.removeItem(STORAGE_KEY);
      return mod;
    } catch (err) {
      const alreadyReloaded = window.sessionStorage.getItem(STORAGE_KEY);
      if (!alreadyReloaded) {
        window.sessionStorage.setItem(STORAGE_KEY, '1');
        window.location.reload();
        // Return a never-resolving promise while the page reloads.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}

// Pages — lazy-loaded for route-level code splitting
const IndexPage = lazyWithRetry(() => import('@/pages/index'));
const AboutPage = lazyWithRetry(() => import('@/pages/about'));
const CartPage = lazyWithRetry(() => import('@/pages/cart'));
const CheckoutPage = lazyWithRetry(() => import('@/pages/checkout'));
const ContactPage = lazyWithRetry(() => import('@/pages/contact'));
const FaqPage = lazyWithRetry(() => import('@/pages/faq'));
const FoodPage = lazyWithRetry(() => import('@/pages/food'));
const JoinPage = lazyWithRetry(() => import('@/pages/join'));
const LoginPage = lazyWithRetry(() => import('@/pages/login'));
const PasswordRecoveryPage = lazyWithRetry(() => import('@/pages/passwordrecovery'));
const PasswordRecoveryConfirmPage = lazyWithRetry(() => import('@/pages/passwordrecovery/confirm'));
const PrivacyPage = lazyWithRetry(() => import('@/pages/privacy'));
const ProfilePage = lazyWithRetry(() => import('@/pages/profile'));
const RegisterPage = lazyWithRetry(() => import('@/pages/register'));
const RequestDemoPage = lazyWithRetry(() => import('@/pages/request-demo'));
const SearchPage = lazyWithRetry(() => import('@/pages/search'));
const TermsPage = lazyWithRetry(() => import('@/pages/terms-of-conduct-topics'));
const FeaturedProductCampaignPage = lazyWithRetry(() => import('@/pages/featured-products/[slug]'));

const CatalogIndexPage = lazyWithRetry(() => import('@/pages/catalog/index'));
const CatalogSlugPage = lazyWithRetry(() => import('@/pages/catalog/[slug]'));
const ProductsSlugPage = lazyWithRetry(() => import('@/pages/products/[slug]'));
const VendorIndexPage = lazyWithRetry(() => import('@/pages/vendor/index'));
const VendorSlugPage = lazyWithRetry(() => import('@/pages/vendor/slug'));
const ManufacturersIndexPage = lazyWithRetry(() => import('@/pages/manufacturers/index'));
const ManufacturersSlugPage = lazyWithRetry(() => import('@/pages/manufacturers/[slug]'));

const OrderIndexPage = lazyWithRetry(() => import('@/pages/order/index'));
const OrderSlugPage = lazyWithRetry(() => import('@/pages/order/slug'));
const OrderConfirmationPage = lazyWithRetry(() => import('@/pages/order/confirmation'));
const OrderPaidPage = lazyWithRetry(() => import('@/pages/order/orderpaid'));
const OrderCancelPage = lazyWithRetry(() => import('@/pages/order/ordercancel'));

const MyAccountPage = lazyWithRetry(() => import('@/pages/myaccount/index'));
const MyAccountAddressPage = lazyWithRetry(() => import('@/pages/myaccount/address'));
const MyAccountChangePasswordPage = lazyWithRetry(() => import('@/pages/myaccount/changepassword'));
const MyAccountCustomerInfoPage = lazyWithRetry(() => import('@/pages/myaccount/customerinfo'));

const ShopifyAppIndexPage = lazyWithRetry(() => import('@/pages/apps/shopify/index'));
const ShopifyDashboardPage = lazyWithRetry(() => import('@/pages/apps/shopify/dashboard'));
const ShopifyOnboardingPage = lazyWithRetry(() => import('@/pages/apps/shopify/onboarding'));
const ShopifyPreferencesPage = lazyWithRetry(() => import('@/pages/apps/shopify/preferences'));
const ShopifyLinkPage = lazyWithRetry(() => import('@/pages/apps/shopify/link'));

const ContentPage = lazyWithRetry(() => import('@/components/ContentPage'));
const BecomeASupplierPage = lazyWithRetry(() => import('@/pages/become-a-supplier'));
const AssociationProgramPage = lazyWithRetry(() => import('@/pages/association-program'));
const PartnerDirectoryPage = lazyWithRetry(() => import('@/pages/partner-directory'));
const CraftRoadmapsPage = lazyWithRetry(() => import('@/pages/craft-roadmaps'));
const SecondLocationRoadmapPage = lazyWithRetry(() => import('@/pages/craft-roadmaps/second-location'));
const CraftBusinessHub = lazyWithRetry(() => import('@/pages/craft-business-hub'));
const ReferralPage = lazyWithRetry(() => import('@/pages/referral'));
const ContractCoPackingDirectoryPage = lazyWithRetry(() => import('@/pages/contract-co-packing-directory'));
const ChampionsProgramPage = lazyWithRetry(() => import('@/pages/champions-program'));
const ChampionsProgramBackupPage = lazyWithRetry(() => import('@/pages/champions-program-backup'));
const EventsPage = lazyWithRetry(() => import('@/pages/events'));
const BlogPage = lazyWithRetry(() => import('@/pages/blog'));
const BlogPostPage = lazyWithRetry(() => import('@/pages/blog/[slug]'));
const PortalLoginPage = lazyWithRetry(() => import('@/pages/portal/login'));
const PortalRegisterPage = lazyWithRetry(() => import('@/pages/portal/register'));
const PortalDashboardPage = lazyWithRetry(() => import('@/pages/portal/dashboard'));
const PortalHomePage = lazyWithRetry(() => import('@/pages/portal/home'));
const PortalProfilePage = lazyWithRetry(() => import('@/pages/portal/profile'));

const PortalOrdersPage = lazyWithRetry(() => import('@/pages/portal/orders'));
const PortalServicesPage = lazyWithRetry(() => import('@/pages/portal/services'));
const PortalApplicationsPage = lazyWithRetry(() => import('@/pages/portal/applications'));
const PortalReportsPage = lazyWithRetry(() => import('@/pages/portal/reports'));
const PortalDirectoryPage = lazyWithRetry(() => import('@/pages/portal/directory'));
const PortalCoMarketingPage = lazyWithRetry(() => import('@/pages/portal/co-marketing'));
const PortalSettingsPage = lazyWithRetry(() => import('@/pages/portal/settings'));
const PortalGoLivePage = lazyWithRetry(() => import('@/pages/portal/go-live'));
const PortalTempPreviewPage = lazyWithRetry(() => import('@/pages/portal/temp-preview'));
const PortalForgotPasswordPage = lazyWithRetry(() => import('@/pages/portal/forgot-password'));
const PortalResetPasswordPage = lazyWithRetry(() => import('@/pages/portal/reset-password'));
const PortalChangePasswordPage = lazyWithRetry(() => import('@/pages/portal/change-password'));
const AccessDeniedPage = lazyWithRetry(() => import('@/pages/access-denied'));
const AdminLoginPage = lazyWithRetry(() => import('@/pages/admin/login'));
const AdminSetupPage = lazyWithRetry(() => import('@/pages/admin/setup'));
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/dashboard'));
const AdminBlogPage = lazyWithRetry(() => import('@/pages/admin/blog'));
const AdminDirectoryPage = lazyWithRetry(() => import('@/pages/admin/directory'));
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/users'));

const PopupHost = lazyWithRetry(() => import('@/components/popups/PopupHost'));
const AdminPopupsPage = lazyWithRetry(() => import('@/pages/admin/popups'));
const AdminPaymentsPage = lazyWithRetry(() => import('@/pages/admin/payments'));
const AdminMasterSupplierListPage = lazyWithRetry(() => import('@/pages/admin/master-supplier-list'));
const AdminJobsPage = lazyWithRetry(() => import('@/pages/admin/jobs'));
const JobsPage = lazyWithRetry(() => import('@/pages/jobs'));

function DefaultLayout() {
  return (
    <div className="flex flex-col min-h-screen overflow-x-clip w-full max-w-[100vw]">
      <div className="flex-grow w-full min-w-0">
        <Navbar />
        <Toaster position="top-center" richColors theme="dark" />
        <Outlet />
        <Suspense fallback={null}>
          <PopupHost />
        </Suspense>
      </div>
      <Footer />
    </div>
  );
}

function ShopifyAppLayout() {
  return (
    <ShopifyLayout>
      <Outlet />
    </ShopifyLayout>
  );
}

function PortalShellLayout() {
  return (
    <RequirePortalAuth>
      <PortalLayout>
        <Outlet />
      </PortalLayout>
    </RequirePortalAuth>
  );
}

export default function App() {
  const [qc] = React.useState(() => createQueryClient());

  return (
    <Provider store={store}>
      <ReduxHydrator />
      <QueryClientProvider client={qc}>
        <HydrationBoundary state={undefined}>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange
          >
            <PortalAuthProvider>
            <BrowserRouter>
              <ScrollToTopOnRouteChange />
              <ScrollToHashOnNavigation />
              <RouteAnalyticsTracker />
              <TrackingCapture />
              <AuthHashRedirector />
              <Suspense fallback={
                <div className="min-h-screen bg-background animate-pulse motion-reduce:animate-none" role="status" aria-live="polite">
                  <span className="sr-only">Loading…</span>
                </div>
              }>
              <Routes>
                {/* Shopify embedded app — no Navbar/Footer */}
                <Route element={<ShopifyAppLayout />}>
                  <Route path="/apps/shopify" element={<ShopifyAppIndexPage />} />
                  <Route path="/apps/shopify/dashboard" element={<ShopifyDashboardPage />} />
                  <Route path="/apps/shopify/onboarding" element={<ShopifyOnboardingPage />} />
                  <Route path="/apps/shopify/preferences" element={<ShopifyPreferencesPage />} />
                  <Route path="/apps/shopify/link" element={<ShopifyLinkPage />} />
                </Route>

                {/* Standalone pages with own Navbar/Footer */}
                <Route path="/about" element={<AboutPage />} />


                {/* Standard pages — with Navbar/Footer */}
                <Route element={<DefaultLayout />}>
                  <Route path="/" element={<IndexPage />} />
                  <Route path="/featured-products/:slug" element={<FeaturedProductCampaignPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/food" element={<FoodPage />} />
                  <Route path="/jobs" element={<JobsPage />} />
                  <Route path="/join" element={<JoinPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/passwordrecovery" element={<PasswordRecoveryPage />} />
                  <Route path="/passwordrecovery/confirm" element={<PasswordRecoveryConfirmPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/request-demo" element={<RequestDemoPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/terms-of-conduct-topics" element={<TermsPage />} />

                  <Route path="/catalog" element={<CatalogIndexPage />} />
                  <Route path="/catalog/*" element={<CatalogSlugPage />} />
                  <Route path="/products/:slug" element={<ProductsSlugPage />} />
                  <Route path="/vendor" element={<VendorIndexPage />} />
                  <Route path="/vendor/:slug" element={<VendorSlugPage />} />
                  <Route path="/manufacturers" element={<ManufacturersIndexPage />} />
                  <Route path="/manufacturers/:slug" element={<ManufacturersSlugPage />} />

                  <Route path="/order" element={<OrderIndexPage />} />
                  <Route path="/order/:id" element={<OrderSlugPage />} />
                  <Route path="/order/:id/cancel" element={<OrderCancelPage />} />
                  <Route path="/order/confirmation" element={<OrderConfirmationPage />} />
                  <Route path="/order/orderpaid" element={<OrderPaidPage />} />

                  <Route path="/myaccount" element={<MyAccountPage />} />
                  <Route path="/myaccount/address" element={<MyAccountAddressPage />} />
                  <Route path="/myaccount/changepassword" element={<MyAccountChangePasswordPage />} />
                  <Route path="/myaccount/customerinfo" element={<MyAccountCustomerInfoPage />} />

                  <Route path="/become-a-supplier" element={<BecomeASupplierPage />} />
                  <Route path="/association-program" element={<AssociationProgramPage />} />
                  {/* Generic content pages — rendered from Velite markdown by slug.
                      To add a new marketing page, create a content/pages/*.md file
                      and add a route here pointing to ContentPage with the slug. */}
                  <Route path="/craft-business-hub" element={<CraftBusinessHub />} />
                  <Route path="/referral" element={<ReferralPage />} />
                  <Route path="/craft-roadmaps" element={<CraftRoadmapsPage />} />
                  <Route path="/craft-roadmaps/second-location" element={<SecondLocationRoadmapPage />} />
                  <Route path="/partner-directory" element={<PartnerDirectoryPage />} />
                  <Route path="/contract-co-packing-directory" element={<ContractCoPackingDirectoryPage />} />
                  <Route path="/champions-program" element={<ChampionsProgramPage />} />
                  {/* Hidden internal backup — Champions Program Backup - May 2026 */}
                  <Route path="/champions-program-backup" element={<ChampionsProgramBackupPage />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/portal/login" element={<PortalLoginPage />} />
                  <Route path="/portal/register" element={<PortalRegisterPage />} />
                  <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
                  <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />
                  {/*
                    Public route: the page itself verifies the magic-link
                    token and gates the form on an active session, so the
                    auth guard would only get in the way of first-time
                    temp-access sign-ins.
                  */}
                  <Route
                    path="/portal/change-password"
                    element={<PortalChangePasswordPage />}
                  />

                  <Route path="/access-denied" element={<AccessDeniedPage />} />

                  {/* Admin auth pages keep their own minimal chrome */}
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin/setup" element={<AdminSetupPage />} />
                </Route>

                {/* Authenticated portal — uses its own sidebar shell, no global Navbar/Footer */}
                <Route element={<PortalShellLayout />}>
                  <Route path="/portal/dashboard" element={<PortalHomePage />} />
                  <Route path="/portal/home" element={<PortalHomePage />} />
                  <Route path="/portal/profile" element={<PortalProfilePage />} />
                  <Route path="/portal/files" element={<Navigate to="/portal/reports" replace />} />
                  <Route path="/portal/orders" element={<PortalOrdersPage />} />
                  <Route path="/portal/services" element={<PortalServicesPage />} />
                  <Route path="/portal/applications" element={<PortalApplicationsPage />} />
                  <Route path="/portal/reports" element={<PortalReportsPage />} />
                  <Route path="/portal/directory" element={<PortalDirectoryPage />} />
                  <Route path="/portal/co-marketing" element={<PortalCoMarketingPage />} />
                  <Route path="/portal/settings" element={<PortalSettingsPage />} />
                  <Route path="/portal/go-live" element={<PortalGoLivePage />} />
                  <Route path="/portal/temp-preview" element={<PortalTempPreviewPage />} />
                  <Route path="/portal/legacy-tabs" element={<PortalDashboardPage />} />
                  <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                  <Route path="/admin/blog" element={<AdminBlogPage />} />
                  <Route path="/admin/directory" element={<AdminDirectoryPage />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/popups" element={<AdminPopupsPage />} />
                  <Route path="/admin/payments" element={<AdminPaymentsPage />} />
                  <Route path="/admin/master-supplier-list" element={<AdminMasterSupplierListPage />} />
                  <Route path="/admin/jobs" element={<AdminJobsPage />} />
                </Route>
              </Routes>
              </Suspense>
            </BrowserRouter>
            </PortalAuthProvider>
          </ThemeProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </Provider>
  );
}
